import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { updateAccountPreferences } from '@/lib/api'
import type { ScreenerParseFilterDefaults, ScreenerParseFilters, ScreenerSettings, User, UserPreferences } from '@/types'

function stringSelection(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) return null
  return [...new Set(value.map((item) => item.trim().toLowerCase()).filter(Boolean))]
}

function parseFilters(value: unknown): ScreenerParseFilters | null {
  if (!value || typeof value !== 'object') return null
  const filters = value as Record<string, unknown>
  const exchanges = stringSelection(filters.exchanges)
  const markets = stringSelection(filters.markets)
  if (!exchanges || !markets || markets.some((market) => market !== 'spot' && market !== 'futures')) return null
  return { exchanges, markets }
}

function parseDefaults(value: unknown): ScreenerParseFilterDefaults | null {
  if (!value || typeof value !== 'object') return null
  const saved = value as Record<string, unknown>
  const chart_filters = parseFilters(saved.chart_filters)
  const orderbook_filters = parseFilters(saved.orderbook_filters)
  return chart_filters && orderbook_filters ? { chart_filters, orderbook_filters } : null
}

function sameFilters(left: ScreenerParseFilters, right: ScreenerParseFilters): boolean {
  return (['exchanges', 'markets'] as const).every((key) =>
    left[key].length === right[key].length && left[key].every((value) => right[key].includes(value)))
}

interface Options {
  user: User | null
  selectedExchanges: string[]
  setSelectedExchanges: (exchanges: string[]) => void
  chartFilters: ScreenerParseFilters
  orderbookFilters: ScreenerParseFilters
  hasLoadedSettings: boolean
  updateSettings: (settings: Partial<ScreenerSettings>) => Promise<boolean>
  discoveryExchanges?: string[]
  setDiscoveryExchanges?: (exchanges: string[]) => void
  hasHydratedDiscoveryPrefs?: boolean
}

export interface ParseFilterChanges {
  chartExchanges?: string[]
  chartMarkets?: string[]
  orderbookExchanges?: string[]
  orderbookMarkets?: string[]
}

export function useScreenerFilterDefaults({
  user, selectedExchanges, setSelectedExchanges, chartFilters, orderbookFilters,
  hasLoadedSettings, updateSettings, discoveryExchanges, setDiscoveryExchanges,
  hasHydratedDiscoveryPrefs = false,
}: Options) {
  const queryClient = useQueryClient()
  const username = user?.username ?? null
  const activeUsername = useRef(username)
  activeUsername.current = username
  const restoredUsername = useRef<string | null>(null)
  const restoredDiscoveryUsername = useRef<string | null>(null)
  const parsePending = useRef(false)
  const saveRequests = useRef({ display: 0, parse: 0, discovery: 0 })
  const [applying, setApplying] = useState(false)
  const [savingDisplay, setSavingDisplay] = useState(false)
  const [savingParse, setSavingParse] = useState(false)
  const [savingDiscovery, setSavingDiscovery] = useState(false)
  const [displayError, setDisplayError] = useState<string | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [discoveryError, setDiscoveryError] = useState<string | null>(null)
  const savedExchanges = useMemo(() => stringSelection(user?.preferences?.screener_display_exchanges),
    [user?.preferences?.screener_display_exchanges])
  const savedParseFilters = useMemo(() => parseDefaults(user?.preferences?.screener_parse_filters),
    [user?.preferences?.screener_parse_filters])
  const savedDiscoveryExchanges = useMemo(() => stringSelection(user?.preferences?.screener_discovery_exchanges),
    [user?.preferences?.screener_discovery_exchanges])

  useEffect(() => {
    restoredUsername.current = null
    restoredDiscoveryUsername.current = null
    saveRequests.current.display += 1
    saveRequests.current.parse += 1
    saveRequests.current.discovery += 1
    setSavingDisplay(false)
    setSavingParse(false)
    setSavingDiscovery(false)
    setDisplayError(null)
    setParseError(null)
    setDiscoveryError(null)
  }, [username])

  // Account defaults take precedence after local discovery settings hydrate,
  // while later edits and auth refreshes keep this visit's temporary choices.
  useEffect(() => {
    if (!username || !hasHydratedDiscoveryPrefs || !setDiscoveryExchanges
      || restoredDiscoveryUsername.current === username) return
    restoredDiscoveryUsername.current = username
    if (savedDiscoveryExchanges !== null) setDiscoveryExchanges([...savedDiscoveryExchanges])
  }, [username, hasHydratedDiscoveryPrefs, savedDiscoveryExchanges, setDiscoveryExchanges])

  const applyParseFilters = useCallback(async (filters: ScreenerParseFilterDefaults) => {
    if (parsePending.current) return false
    parsePending.current = true
    setApplying(true)
    setParseError(null)
    try {
      const ok = await updateSettings({
        ...filters,
        exchanges: filters.chart_filters.exchanges,
        markets: filters.chart_filters.markets,
      })
      if (!ok && activeUsername.current === username) setParseError('Could not apply parse filters. Please try again.')
      return ok
    } catch {
      if (activeUsername.current === username) setParseError('Could not apply parse filters. Please try again.')
      return false
    } finally {
      parsePending.current = false
      setApplying(false)
    }
  }, [updateSettings, username])

  // Restore once when this account opens the screener. Saving or refreshing the
  // auth query must not discard temporary filter changes during this visit.
  useEffect(() => {
    if (!username || !hasLoadedSettings || restoredUsername.current === username || parsePending.current) return
    restoredUsername.current = username
    setDisplayError(null)
    setParseError(null)
    if (savedParseFilters && (!sameFilters(chartFilters, savedParseFilters.chart_filters)
      || !sameFilters(orderbookFilters, savedParseFilters.orderbook_filters))) {
      void applyParseFilters(savedParseFilters)
    }
  }, [username, hasLoadedSettings, savedParseFilters, chartFilters, orderbookFilters, applying, applyParseFilters])

  const save = async (preferences: UserPreferences, kind: 'display' | 'parse' | 'discovery') => {
    if (!username) return
    const setSaving = { display: setSavingDisplay, parse: setSavingParse, discovery: setSavingDiscovery }[kind]
    const setError = { display: setDisplayError, parse: setParseError, discovery: setDiscoveryError }[kind]
    const requestId = ++saveRequests.current[kind]
    const isCurrent = () => activeUsername.current === username && saveRequests.current[kind] === requestId
    setSaving(true)
    setError(null)
    try {
      const result = await updateAccountPreferences(preferences)
      if (!isCurrent()) return
      if (!result.ok) throw new Error(result.error || 'Could not save filters. Please try again.')
      queryClient.setQueryData<User | null>(['auth', 'me'], (current) => {
        if (!current || current.username !== username) return current
        return { ...current, preferences: { ...current.preferences, ...preferences } }
      })
    } catch (error) {
      if (isCurrent()) {
        setError(error instanceof Error ? error.message : 'Could not save filters. Please try again.')
      }
    } finally {
      if (isCurrent()) setSaving(false)
    }
  }

  return {
    savedExchanges,
    savedDiscoveryExchanges,
    parseDisabled: !username || !hasLoadedSettings || applying || savingParse,
    updateParseFilters: (next: ParseFilterChanges) => applyParseFilters({
      chart_filters: {
        exchanges: next.chartExchanges ?? chartFilters.exchanges,
        markets: next.chartMarkets ?? chartFilters.markets,
      },
      orderbook_filters: {
        exchanges: next.orderbookExchanges ?? orderbookFilters.exchanges,
        markets: next.orderbookMarkets ?? orderbookFilters.markets,
      },
    }),
    displayDefaults: {
      hasSaved: savedExchanges !== null,
      saving: savingDisplay || !username,
      error: displayError,
      onSave: () => { void save({ screener_display_exchanges: [...selectedExchanges] }, 'display') },
      onRestore: () => {
        if (savedExchanges) {
          setSelectedExchanges([...savedExchanges])
          setDisplayError(null)
        }
      },
    },
    parseDefaults: {
      hasSaved: savedParseFilters !== null,
      saving: savingParse,
      error: parseError,
      onSave: () => { void save({ screener_parse_filters: {
        chart_filters: chartFilters, orderbook_filters: orderbookFilters,
      } }, 'parse') },
      onRestore: () => { if (savedParseFilters) void applyParseFilters(savedParseFilters) },
    },
    discoveryDefaults: {
      hasSaved: savedDiscoveryExchanges !== null,
      saving: savingDiscovery || !username || !hasHydratedDiscoveryPrefs,
      error: discoveryError,
      onSave: () => {
        if (discoveryExchanges && hasHydratedDiscoveryPrefs) {
          void save({ screener_discovery_exchanges: [...discoveryExchanges] }, 'discovery')
        }
      },
      onRestore: () => {
        if (savedDiscoveryExchanges !== null && setDiscoveryExchanges) {
          setDiscoveryExchanges([...savedDiscoveryExchanges])
          setDiscoveryError(null)
        }
      },
    },
  }
}
